/**
 * BetaApplicationList Component
 *
 * Administrative interface for viewing and managing beta applications.
 * Provides comprehensive list view with filtering, sorting, and status management.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';

import { AdminLayout } from '@/components/shared/layouts';
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Chip,
  Spinner,
  Select,
  SelectItem,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  useDisclosure
} from '@enfomentis/ui';
import { AGGridTable } from '@enfomentis/ui';
import { betaApplicationService } from '@/services/BetaApplicationService';
import { communicationService } from '@/services/CommunicationService';
import { Brand } from '@enfomentis/shared/config';
import { EmailManagementModal } from './EmailManagementModal';
import type {
  BetaApplication,
  BetaApplicationStatus
} from '@/types/supabase/BetaApplication';
import { RefreshCw, Filter, BarChart3, Mail } from 'lucide-react';

/**
 * Props for BetaApplicationList component
 */
export interface BetaApplicationListProps {
  /** Optional title override */
  title?: string;
}

/**
 * Status filter options
 */
const STATUS_FILTER_OPTIONS = [
  { key: 'all', label: 'All Applications' },
  { key: 'pending', label: 'Pending Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'waitlisted', label: 'Waitlisted' },
  { key: 'rejected', label: 'Rejected' }
] as const;

/**
 * Beta Application List Component
 *
 * Features:
 * - Professional data grid with AG Grid
 * - Real-time data from Supabase
 * - Status filtering and indicators
 * - Responsive design for admin interface
 * - Loading states and error handling
 * - Foundation for detail view integration (Task 3D)
 *
 * Usage:
 * ```tsx
 * <BetaApplicationList title="Beta Applications" />
 * ```
 */
export const BetaApplicationList: React.FC<BetaApplicationListProps> = ({
  title = "Beta Applications"
}) => {
  const navigate = useNavigate();

  // State management
  const [applications, setApplications] = useState<BetaApplication[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [emailCommunications, setEmailCommunications] = useState<Map<string, {
    sent: number;
    lastSent?: Date;
    byType: {
      confirmation: { count: number; lastSent?: Date; };
      approval: { count: number; lastSent?: Date; };
      rejection: { count: number; lastSent?: Date; };
      waitlist: { count: number; lastSent?: Date; };
    }
  }>>(new Map());
  const [statusCounts, setStatusCounts] = useState<Record<BetaApplicationStatus, number>>({
    pending: 0,
    approved: 0,
    waitlisted: 0,
    rejected: 0,
    activated: 0
  });

  // Modal for mobile stats
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Email management modal
  const { isOpen: isEmailManagementModalOpen, onOpen: openEmailManagementModal, onClose: closeEmailManagementModal } = useDisclosure();

  // Load data
  const loadApplications = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const [applicationsData, countsData] = await Promise.all([
        betaApplicationService.getAll(),
        betaApplicationService.getStatusCounts()
      ]);

      // Load email communications for each application
      const emailCommMap = new Map<string, {
        sent: number;
        lastSent?: Date;
        byType: {
          confirmation: { count: number; lastSent?: Date; };
          approval: { count: number; lastSent?: Date; };
          rejection: { count: number; lastSent?: Date; };
          waitlist: { count: number; lastSent?: Date; };
        }
      }>();

      await Promise.all(
        applicationsData.map(async (app) => {
          try {
            const communications = await communicationService.getByEntity('beta_application', app.id);
            const sentEmails = communications.filter(comm => comm.status === 'sent');
            const lastSentComm = sentEmails
              .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())[0];

            // Count emails by template type and track last sent dates
            const byType: {
              confirmation: { count: number; lastSent?: Date; };
              approval: { count: number; lastSent?: Date; };
              rejection: { count: number; lastSent?: Date; };
              waitlist: { count: number; lastSent?: Date; };
            } = {
              confirmation: { count: 0 },
              approval: { count: 0 },
              rejection: { count: 0 },
              waitlist: { count: 0 }
            };

            sentEmails.forEach(comm => {
              const templateType = comm.templateSnapshot?.template_type;
              const sentDate = new Date(comm.sentAt);

              if (templateType === 'confirmation') {
                byType.confirmation.count++;
                if (!byType.confirmation.lastSent || sentDate > byType.confirmation.lastSent) {
                  byType.confirmation.lastSent = sentDate;
                }
              } else if (templateType === 'approval') {
                byType.approval.count++;
                if (!byType.approval.lastSent || sentDate > byType.approval.lastSent) {
                  byType.approval.lastSent = sentDate;
                }
              } else if (templateType === 'rejection') {
                byType.rejection.count++;
                if (!byType.rejection.lastSent || sentDate > byType.rejection.lastSent) {
                  byType.rejection.lastSent = sentDate;
                }
              } else if (templateType === 'waitlist') {
                byType.waitlist.count++;
                if (!byType.waitlist.lastSent || sentDate > byType.waitlist.lastSent) {
                  byType.waitlist.lastSent = sentDate;
                }
              }
            });

            const commData = {
              sent: sentEmails.length,
              byType,
              ...(lastSentComm && { lastSent: new Date(lastSentComm.sentAt) })
            };

            emailCommMap.set(app.id, commData);
          } catch (commError) {
            console.error(`Failed to load communications for ${app.email}:`, commError);
            // Set default values if communication loading fails
            emailCommMap.set(app.id, {
              sent: 0,
              byType: {
                confirmation: { count: 0 },
                approval: { count: 0 },
                rejection: { count: 0 },
                waitlist: { count: 0 }
              }
            });
          }
        })
      );

      setApplications(applicationsData);
      setStatusCounts(countsData);
      setEmailCommunications(emailCommMap);
    } catch (err) {
      console.error('Failed to load beta applications:', err);
      setError('Failed to load beta applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadApplications();
  }, []);

  // Filter applications based on status
  const filteredApplications = useMemo(() => {
    if (statusFilter === 'all') {
      return applications;
    }
    return applications.filter(app => app.status === statusFilter);
  }, [applications, statusFilter]);

  // Status chip component
  const StatusBadge: React.FC<{ status: BetaApplicationStatus }> = ({ status }) => {
    const variants = {
      pending: { color: 'warning' as const, text: 'Pending Review' },
      approved: { color: 'success' as const, text: 'Approved' },
      waitlisted: { color: 'secondary' as const, text: 'Waitlisted' },
      rejected: { color: 'danger' as const, text: 'Rejected' },
      activated: { color: 'primary' as const, text: 'Activated' }
    };

    const variant = variants[status];
    return (
      <Chip color={variant.color} variant="flat" size="sm">
        {variant.text}
      </Chip>
    );
  };

  // Detect if we're on mobile and fullscreen mode
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isFullScreen, setIsFullScreen] = useState(() => {
    const stored = localStorage.getItem(`${Brand.cachePrefix}-fullscreen`);
    return stored ? JSON.parse(stored) : false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const handleStorageChange = () => {
      const stored = localStorage.getItem(`${Brand.cachePrefix}-fullscreen`);
      setIsFullScreen(stored ? JSON.parse(stored) : false);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('storage', handleStorageChange);

    // Also listen for direct localStorage changes in same tab
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, [key, value]);
      if (key === `${Brand.cachePrefix}-fullscreen`) {
        handleStorageChange();
      }
    };

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('storage', handleStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  // AG Grid column definitions - conditional based on mobile/desktop
  const columnDefs: ColDef<BetaApplication>[] = useMemo(() => [
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      cellRenderer: (params: ICellRendererParams<BetaApplication>) => <StatusBadge status={params.value} />,
      filter: !isMobile,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'fullName',
      headerName: 'Full Name',
      width: 200,
      filter: !isMobile,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      filter: !isMobile,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'primaryFocus',
      headerName: 'Trading Focus',
      width: 200,
      cellStyle: { display: 'flex', alignItems: 'center' },
      valueFormatter: (params: { value: string[] }): string => {
        const focuses = params.value as string[];
        if (!focuses || focuses.length === 0) return '—';
        if (focuses.length === 1) return focuses[0] || '—';
        return `${focuses[0]} +${focuses.length - 1}`;
      },
      cellRenderer: (params: ICellRendererParams<BetaApplication>) => {
        const focuses = params.value as string[];
        if (!focuses || focuses.length === 0) return '—';

        if (focuses.length === 1) {
          return <Chip size="sm" variant="flat">{focuses[0]}</Chip>;
        }

        return (
          <div className="flex gap-1 flex-wrap">
            <Chip size="sm" variant="flat">{focuses[0]}</Chip>
            {focuses.length > 1 && (
              <Chip size="sm" variant="flat" color="secondary">
                +{focuses.length - 1}
              </Chip>
            )}
          </div>
        );
      },
      filter: false,
      sortable: false,
      resizable: !isMobile
    },
    {
      field: 'experienceLevel',
      headerName: 'Experience',
      width: 150,
      cellStyle: { display: 'flex', alignItems: 'center' },
      cellRenderer: (params: ICellRendererParams<BetaApplication>) => (
        <Chip size="sm" variant="bordered" color="primary">
          {params.value}
        </Chip>
      ),
      filter: !isMobile,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'createdAt',
      headerName: 'Submitted',
      width: 150,
      cellRenderer: (params: ICellRendererParams<BetaApplication>) => {
        const date = new Date(params.value);
        return date.toLocaleDateString();
      },
      filter: !isMobile ? 'agDateColumnFilter' : false,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'reviewedBy',
      headerName: 'Reviewed By',
      width: 150,
      cellRenderer: (params: ICellRendererParams<BetaApplication>) => params.value || '—',
      filter: !isMobile,
      sortable: true,
      resizable: !isMobile
    },
    {
      field: 'reviewedAt',
      headerName: 'Review Date',
      width: 150,
      cellRenderer: (params: ICellRendererParams<BetaApplication>) => {
        if (!params.value) return '—';
        const date = new Date(params.value);
        return date.toLocaleDateString();
      },
      filter: !isMobile ? 'agDateColumnFilter' : false,
      sortable: true,
      resizable: !isMobile
    },
    {
      colId: 'confirmation_email',
      headerName: 'Confirmation',
      width: 160,
      cellRenderer: (params: ICellRendererParams<BetaApplication>) => {
        const application = params.data;
        if (!application) return null;

        const emailData = emailCommunications.get(application.id);
        const confirmationData = emailData?.byType?.confirmation;

        if (!confirmationData || confirmationData.count === 0) {
          return (
            <Chip size="sm" variant="flat" color="default">
              None
            </Chip>
          );
        }

        const dateText = confirmationData.lastSent
          ? confirmationData.lastSent.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })
          : '';

        return (
          <Chip size="sm" variant="flat" color="primary">
            Sent {dateText}
          </Chip>
        );
      },
      filter: false,
      sortable: false,
      resizable: !isMobile
    },
    {
      colId: 'approval_email',
      headerName: 'Approval',
      width: 160,
      cellRenderer: (params: ICellRendererParams<BetaApplication>) => {
        const application = params.data;
        if (!application) return null;

        const emailData = emailCommunications.get(application.id);
        const approvalData = emailData?.byType?.approval;

        if (!approvalData || approvalData.count === 0) {
          return (
            <Chip size="sm" variant="flat" color="default">
              None
            </Chip>
          );
        }

        const dateText = approvalData.lastSent
          ? approvalData.lastSent.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })
          : '';

        return (
          <Chip size="sm" variant="flat" color="success">
            Sent {dateText}
          </Chip>
        );
      },
      filter: false,
      sortable: false,
      resizable: !isMobile
    },
    {
      colId: 'rejection_email',
      headerName: 'Rejection',
      width: 160,
      cellRenderer: (params: ICellRendererParams<BetaApplication>) => {
        const application = params.data;
        if (!application) return null;

        const emailData = emailCommunications.get(application.id);
        const rejectionData = emailData?.byType?.rejection;

        if (!rejectionData || rejectionData.count === 0) {
          return (
            <Chip size="sm" variant="flat" color="default">
              None
            </Chip>
          );
        }

        const dateText = rejectionData.lastSent
          ? rejectionData.lastSent.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })
          : '';

        return (
          <Chip size="sm" variant="flat" color="danger">
            Sent {dateText}
          </Chip>
        );
      },
      filter: false,
      sortable: false,
      resizable: !isMobile
    },
    {
      colId: 'waitlist_email',
      headerName: 'Waitlist',
      width: 160,
      cellRenderer: (params: ICellRendererParams<BetaApplication>) => {
        const application = params.data;
        if (!application) return null;

        const emailData = emailCommunications.get(application.id);
        const waitlistData = emailData?.byType?.waitlist;

        if (!waitlistData || waitlistData.count === 0) {
          return (
            <Chip size="sm" variant="flat" color="default">
              None
            </Chip>
          );
        }

        const dateText = waitlistData.lastSent
          ? waitlistData.lastSent.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })
          : '';

        return (
          <Chip size="sm" variant="flat" color="warning">
            Sent {dateText}
          </Chip>
        );
      },
      filter: false,
      sortable: false,
      resizable: !isMobile
    },
    {
      colId: 'details',
      headerName: 'Details',
      width: 100,
      cellRenderer: (params: ICellRendererParams<BetaApplication>) => (
        <Button
          size="sm"
          variant="bordered"
          color="primary"
          onPress={() => navigate(`/admin/beta-applications/${params.data?.id}`)}
          className="my-1"
        >
          View
        </Button>
      ),
      filter: false,
      sortable: false,
      resizable: false,
      pinned: 'left'
    }
  ], [isMobile, emailCommunications, navigate]);

  // Grid options - conditional based on mobile/desktop
  const gridOptions = {
    defaultColDef: {
      resizable: !isMobile,
      sortable: true,
      filter: !isMobile
    },
    rowSelection: {
      mode: 'singleRow',
      checkboxes: false,
      enableClickSelection: true
    } as const,
    pagination: true,
    paginationPageSize: isMobile ? 25 : 50,
    paginationPageSizeSelector: isMobile ? false : [25, 50, 100],
    animateRows: true,
    enableCellTextSelection: true,
    suppressMenuHide: isMobile
  };

  return (
    <AdminLayout title={title}>
      <Card className="space-y-6" radius="none" shadow="none">
        {/* Overview Cards - Hidden on mobile */}
        <CardHeader className="hidden md:grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card shadow="sm">
            <CardBody className="text-center py-4">
              <p className="text-2xl font-bold text-warning">{statusCounts.pending}</p>
              <p className="text-sm text-text-secondary">Pending</p>
            </CardBody>
          </Card>
          <Card shadow="sm">
            <CardBody className="text-center py-4">
              <p className="text-2xl font-bold text-success">{statusCounts.approved}</p>
              <p className="text-sm text-text-secondary">Approved</p>
            </CardBody>
          </Card>
          <Card shadow="sm">
            <CardBody className="text-center py-4">
              <p className="text-2xl font-bold text-secondary">{statusCounts.waitlisted}</p>
              <p className="text-sm text-text-secondary">Waitlisted</p>
            </CardBody>
          </Card>
          <Card shadow="sm">
            <CardBody className="text-center py-4">
              <p className="text-2xl font-bold text-danger">{statusCounts.rejected}</p>
              <p className="text-sm text-text-secondary">Rejected</p>
            </CardBody>
          </Card>
        </CardHeader>

        <CardBody className="space-y-6">
        {/* Main Applications Table */}
        <Card shadow="none" radius="none" className='flex-shrink-0'>
          <CardHeader>
            <div className="flex items-center justify-between w-full">
              {/* Desktop filter */}
              <div className="hidden md:flex items-center gap-2">
                <Filter size={16} className="text-text-secondary" />
                <Select
                  size="sm"
                  selectedKeys={[statusFilter]}
                  onSelectionChange={(keys) => {
                    const selectedKey = Array.from(keys)[0] as string;
                    setStatusFilter(selectedKey || 'all');
                  }}
                  className="min-w-[180px]"
                  aria-label="Filter by status"
                >
                  {STATUS_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.key}>
                      {option.label}
                    </SelectItem>
                  ))}
                </Select>
              </div>

              <div className="flex items-center gap-2 w-full md:w-auto">
                {/* Mobile filter - simplified */}
                <div className="md:hidden">
                  <Select
                    size="sm"
                    selectedKeys={[statusFilter]}
                    onSelectionChange={(keys) => {
                      const selectedKey = Array.from(keys)[0] as string;
                      setStatusFilter(selectedKey || 'all');
                    }}
                    className="min-w-[140px]"
                    aria-label="Filter by status"
                  >
                    {STATUS_FILTER_OPTIONS.map((option) => (
                      <SelectItem key={option.key}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
                <div className="flex-1 md:flex-none"></div>
                <Button
                  size="sm"
                  variant="flat"
                  color="secondary"
                  onPress={openEmailManagementModal}
                  className="sm:min-w-0"
                >
                  <div className="flex items-center gap-2">
                    <Mail size={16} />
                    <span className="hidden sm:inline">Email Management</span>
                  </div>
                </Button>
                <Button
                  size="sm"
                  variant="flat"
                  color="primary"
                  onPress={loadApplications}
                  isLoading={loading}
                  className="sm:min-w-0"
                >
                  <div className="flex items-center gap-2">
                    <RefreshCw size={16} />
                    <span className="hidden sm:inline">Refresh</span>
                  </div>
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            {error && (
              <div className="p-6 text-center">
                <p className="text-danger">{error}</p>
                <Button
                  size="sm"
                  color="primary"
                  onPress={loadApplications}
                  className="mt-4"
                >
                  Retry
                </Button>
              </div>
            )}

            {loading && !error && (
              <div className="flex items-center justify-center py-12">
                <Spinner color="primary" />
                <span className="ml-3 text-text-secondary">Loading applications...</span>
              </div>
            )}

            {!loading && !error && (
              <div className={`min-h-[200px] ${
                isFullScreen
                  ? isMobile
                    ? "h-[calc(100vh-325px)]" // Fullscreen mobile 
                    : "h-[calc(100vh-465px)]" // Fullscreen desktop 
                  : isMobile
                    ? "h-[calc(100vh-375px)]" // Normal mobile with header showing
                    : "md:h-[480px]" // Normal desktop
              }`}>
                <AGGridTable
                  rowData={filteredApplications}
                  columnDefs={columnDefs}
                  gridOptions={gridOptions}
                  height="100%"
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* Mobile Floating Stats Button */}
        <Button
          isIconOnly
          color="primary"
          variant="shadow"
          size="lg"
          onPress={onOpen}
          className="md:hidden fixed bottom-6 left-6 z-50 shadow-lg"
          aria-label="View application statistics"
        >
          <BarChart3 size={24} />
        </Button>

        {/* Mobile Stats Drawer */}
        <Drawer isOpen={isOpen} onClose={onClose} placement="bottom">
          <DrawerContent>
            <DrawerHeader>
              <h3 className="text-lg font-semibold">Application Statistics</h3>
            </DrawerHeader>
            <DrawerBody>
              <div className="grid grid-cols-2 gap-4 pb-4">
                <div className="text-center p-4 bg-warning-50 dark:bg-warning-900/20 rounded-lg">
                  <p className="text-2xl font-bold text-warning">{statusCounts.pending}</p>
                  <p className="text-sm text-text-secondary">Pending</p>
                </div>
                <div className="text-center p-4 bg-success-50 dark:bg-success-900/20 rounded-lg">
                  <p className="text-2xl font-bold text-success">{statusCounts.approved}</p>
                  <p className="text-sm text-text-secondary">Approved</p>
                </div>
                <div className="text-center p-4 bg-secondary-50 dark:bg-secondary-900/20 rounded-lg">
                  <p className="text-2xl font-bold text-secondary">{statusCounts.waitlisted}</p>
                  <p className="text-sm text-text-secondary">Waitlisted</p>
                </div>
                <div className="text-center p-4 bg-danger-50 dark:bg-danger-900/20 rounded-lg">
                  <p className="text-2xl font-bold text-danger">{statusCounts.rejected}</p>
                  <p className="text-sm text-text-secondary">Rejected</p>
                </div>
              </div>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
        </CardBody>
      </Card>

      {/* Email Management Modal */}
      <EmailManagementModal
        isOpen={isEmailManagementModalOpen}
        onClose={closeEmailManagementModal}
      />
    </AdminLayout>
  );
};